import { useState, useRef, useCallback, useEffect } from 'react';
import type * as PIXI from 'pixi.js';
import type { Viewport } from 'pixi-viewport';
import type { RenderFurniture } from '../../../domain/types';
import { getRadialMenuPositions, getClampedMenuCenter, type RadialMenuActionKey } from './radialMenu';
import { playSfx } from '../../../utils/sfx';

// ── Radial menu state machine (decomposition step 04c) ──
// Extracted verbatim from RoomCanvas: menu open/close state, option hit-centers,
// marker visual activation and screen-point action detection. Deps arrays are
// preserved 1:1 (incl. intentionally-frozen closures) to keep runtime behavior
// identical. `showGreenOutline` / `setSelectedFurniture` stay owned by RoomCanvas
// and are passed in as parameters. `roomViewport` is a module-level mutable in
// RoomCanvas, so it is passed as a getter to preserve live-read semantics inside
// the intentionally-frozen closeRadialMenu closure.

const RADIAL_OPTION_HIT_WIDTH = 180; // Збільшено для більших кнопок
const RADIAL_OPTION_HIT_HEIGHT = 34;

export interface UseRadialMenuParams {
  setSelectedFurniture: React.Dispatch<React.SetStateAction<RenderFurniture | null>>;
  showGreenOutline: (marker: PIXI.Container) => void;
  getRoomViewport: () => Viewport | null;
}

export function useRadialMenu({ setSelectedFurniture, showGreenOutline, getRoomViewport }: UseRadialMenuParams) {
  const [showRadialMenu, setShowRadialMenu] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
  const [highlightedRadialAction, setHighlightedRadialAction] = useState<RadialMenuActionKey | null>(null);

  const radialMenuCentersRef = useRef<Array<{ key: RadialMenuActionKey; x: number; y: number }>>([]);
  const radialMenuMarkerStateRef = useRef<{ marker: PIXI.Container; scaleX: number; scaleY: number } | null>(null);

  const detectRadialActionByScreenPoint = useCallback((screenX: number, screenY: number): RadialMenuActionKey | null => {
    let bestKey: RadialMenuActionKey | null = null;
    let bestDist = Infinity;
    for (const option of radialMenuCentersRef.current) {
      if (Math.abs(screenX - option.x) <= RADIAL_OPTION_HIT_WIDTH / 2 &&
          Math.abs(screenY - option.y) <= RADIAL_OPTION_HIT_HEIGHT / 2) {
        const dist = (screenX - option.x) ** 2 + (screenY - option.y) ** 2;
        if (dist < bestDist) {
          bestDist = dist;
          bestKey = option.key;
        }
      }
    }
    return bestKey;
  }, []);

  const activateMarkerMenuVisualState = useCallback((marker: PIXI.Container) => {
    if (!marker || !marker.scale) return;
    const prev = radialMenuMarkerStateRef.current;
    if (prev?.marker === marker) return;
    if (prev?.marker?.scale) {
      prev.marker.scale.set(prev.scaleX, prev.scaleY);
    }
    radialMenuMarkerStateRef.current = {
      marker,
      scaleX: marker.scale.x,
      scaleY: marker.scale.y,
    };
    marker.scale.set(marker.scale.x * 1.06, marker.scale.y * 1.06);
    showGreenOutline(marker);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- freeze showGreenOutline (decomposition: 1:1 prior identity)
  }, []);

  const deactivateMarkerMenuVisualState = useCallback(() => {
    const prev = radialMenuMarkerStateRef.current;
    if (prev?.marker?.scale) {
      prev.marker.scale.set(prev.scaleX, prev.scaleY);
    }
    radialMenuMarkerStateRef.current = null;
    // Note: green outline is NOT removed here — it persists as a selection indicator
    // until the user taps empty space or another item.
  }, []);

  const closeRadialMenu = useCallback(() => {
    setShowRadialMenu(false);
    setSelectedFurniture(null);
    setHighlightedRadialAction(null);
    radialMenuCentersRef.current = [];
    deactivateMarkerMenuVisualState();
    const roomViewport = getRoomViewport();
    if (roomViewport) roomViewport.pause = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- freeze getRoomViewport live-read; setState is stable
  }, [deactivateMarkerMenuVisualState]);

  // ─── Radial menu base ────────────────────────────────────────────────────────
  // openRadialMenuCore handles shared position calculation and state updates.
  // All "open menu" variants are thin wrappers that set their specific state and then call this.
  const openRadialMenuCore = useCallback(
    (options: Array<{ key: RadialMenuActionKey; icon: string; textKey: string }>, pointerX: number, pointerY: number) => {
      const clampedCenter = getClampedMenuCenter({ x: pointerX, y: pointerY }, options.length);
      const positions = getRadialMenuPositions(options.length);
      radialMenuCentersRef.current = options.map((item, i) => ({
        key: item.key,
        x: clampedCenter.x + (positions[i]?.xOffset ?? 0),
        y: clampedCenter.y + (positions[i]?.yOffset ?? 0),
      }));
      setMenuPosition(clampedCenter);
      setShowRadialMenu(true);
      playSfx('menu');
      setHighlightedRadialAction(detectRadialActionByScreenPoint(pointerX, pointerY));
    },
    [detectRadialActionByScreenPoint]
  );

  useEffect(() => {
    return () => {
      deactivateMarkerMenuVisualState();
    };
  }, [deactivateMarkerMenuVisualState]);

  return {
    showRadialMenu,
    menuPosition,
    highlightedRadialAction,
    setHighlightedRadialAction,
    detectRadialActionByScreenPoint,
    activateMarkerMenuVisualState,
    deactivateMarkerMenuVisualState,
    closeRadialMenu,
    openRadialMenuCore,
  };
}
